import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import { SurfaceContext, VRContext } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import vtkLineSource from '@kitware/vtk.js/Filters/Sources/LineSource';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import { Vector3, Vector4 } from '@kitware/vtk.js/types';
import { hexToNormalizedRGB, hexToRgb, rgbToNormalizedRGBA } from 'config/colorUtils';
import { viewportColors } from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import { axesNames } from 'config/render3DConfig';

function computeEndPoints(position: Vector3, direction: Vector3, length: number) {
  const start: [number, number, number] = [
    -position[0] - direction[0] * length,
    position[1] - direction[1] * length,
    position[2] - direction[2] * length,
  ];
  const end: [number, number, number] = [
    -position[0] + direction[0] * length,
    position[1] + direction[1] * length,
    position[2] + direction[2] * length,
  ];
  return { start, end };
}

function addAxesLinesAt(
  name: string,
  position: Vector3,
  direction: Vector3,
  length: number,
  color: [number, number, number, number]
): vtkActor {

  const { start, end } = computeEndPoints(position,direction,length);
  const lineSource = vtkLineSource.newInstance({ point1: start, point2: end });
  const mapper = vtkMapper.newInstance();
  mapper.setInputConnection(lineSource.getOutputPort());

  const actor = vtkActor.newInstance();
  actor.setMapper(mapper);
  actor.getProperty().setColor(color[0], color[1], color[2]);

  return actor;
}

export const clearAxesLinesAt = async ({
  context,
}: {
  context: React.MutableRefObject<VRContext | SurfaceContext | null>;
}) => {
  const current = context.current;
  if (!current || !current.renderer || !current.actorIds) return;
  const { renderer, actorIds } = current;
  ['Sagittal', 'Coronal', 'Axial'].forEach((key) => {
    const actor = actorIds[key];
    if (actor) {
      renderer.removeActor(actor);
      delete actorIds[key];
    }
  });
};

function createAxesActors(
  position: Vector3,
  axes: { x: Vector3; y: Vector3; z: Vector3 },
  length: number
): Record<string, vtkActor> {
  return {
    Sagittal: addAxesLinesAt('Sagittal', position, axes.x, length, rgbToNormalizedRGBA(viewportColors['Sagittal'])),
    Coronal: addAxesLinesAt('Coronal', position, axes.y, length, rgbToNormalizedRGBA(viewportColors['Coronal'])),
    Axial: addAxesLinesAt('Axial', position, axes.z, length, rgbToNormalizedRGBA(viewportColors['Axial'])),
  };
}


function updateLineActor(
  actor: vtkActor,
  position: Vector3,
  direction: Vector3,
  length: number,
  color: [number, number, number, number]
) {
  const { start, end } = computeEndPoints(position,direction,length);
  const mapper = actor.getMapper?.();
  const conn = mapper?.getInputConnection?.(0);
  const src = (conn as any)?.getSource?.();

  if (src?.setPoint1 && src?.setPoint2) {
    src.setPoint1(start);
    src.setPoint2(end);
    src.modified?.();
  } else {
    const poly = mapper?.getInputData?.();
    const pts = poly?.getPoints?.();
    if (pts?.setData) {
      const arr = new Float32Array([
        start[0], start[1], start[2],
        end[0],   end[1],   end[2],
      ]);
      pts.setData(arr, 3);
      pts.modified?.();
      poly.modified?.();
    }
  }
  return actor;
}


export const createAxesLinesAt =  ({
  context,
  axesLength = 100,
}: {
  context: React.MutableRefObject<SurfaceContext|VRContext|null>;
  axesLength?: number;
}) => {
  if (!context.current) return;

  const { renderer,renderWindow,focus,axes,rendererId, actorIds} = context.current;
  if(!renderer) return;
  if(!focus) return;
  if(!axes) return;

  // clearAxesLinesAt({context});

  const oglrw = renderWindow
    ?.getViews?.()[0]
    ?.getRenderable?.();
  const gl = oglrw?.getContext?.();
  if (gl && typeof gl.isContextLost === 'function' && gl.isContextLost()) {
    return; // 컨텍스트 죽었으면 렌더 스킵
  }

  const dirMap: Record<(typeof keys)[number], Vector3> = {
    Sagittal: axes.x,
    Coronal: axes.y,
    Axial: axes.z,
  };
  const keys = Object.values(axesNames) as (typeof axesNames)[keyof typeof axesNames][];
  const visible = context.current.isAxis3DVisible;
  keys.forEach((key) => {
    const rgba = [...hexToNormalizedRGB(viewportColors[key]), 1.0] as Vector4;

    const dir = dirMap[key];
    let actor = context.current?.actorIds[key] as (vtkActor & { lineSource?: vtkLineSource }) | undefined;
    if (!actor) {
      actor = addAxesLinesAt(key, focus as Vector3, dir as Vector3, axesLength, rgba);
      context.current?.renderer.addActor(actor);
    } else {
      actor = updateLineActor(actor, focus as Vector3, dir as Vector3, axesLength, rgba);
    }
    actor?.setVisibility(visible);
    actor.modified?.();
    context.current!.actorIds[key] = actor;
  });

  renderer.resetCameraClippingRange();
  requestAnimationFrame(() => scheduleRender(rendererId!,{type:'updateRenderWindow'}));
};

export function setAxesLinesVisibility({
  context,
  visible,
}: {
  context: React.MutableRefObject<SurfaceContext | VRContext | null>;
  visible: boolean;
}) {
  const current = context.current;
  if (!current || !current.actorIds) return;


   ['Sagittal', 'Coronal', 'Axial'].forEach((key) => {
    const actor = current.actorIds[key];
    if (actor) {
      actor?.setVisibility(visible);
    }
  });

  // if (current.rendererId) {
  //   requestAnimationFrame(() => scheduleRender(current.rendererId!, { type: 'updateRenderWindow' }));
  // }
}
